import { Descriptions, Modal } from 'antd';
import { DescriptionsItemProps } from 'antd/es/descriptions/Item';
import moment from 'moment';
import { useViewport } from '@hooks/viewport';
import { PaymentStatementRefundItem, PaymentStatementRefundStatus } from '@models/paymentStatement';
import { Purchase, SalesChannelsType } from '@models/sales-channels';
import useProductOrderDetailModalStore from '@stores/useProductOrderDetailModalStore';
import { getChannelText, getPaymentAmountWithMembershipDiscountText } from '@utils/common';
import { formatPhoneNumber } from '@utils/dataTransform';
import { combinedRefundItemsByName } from '@utils/payment';

const renderPurchases = (purchases: PaymentStatementRefundItem[] | Purchase[] = [], channel?: SalesChannelsType) => {
  const isNaverOrder = channel === SalesChannelsType.NAVER_STORE;
  return (purchases || []).map((item) => {
    let curCount = item.count || 0;
    if (!isNaverOrder) {
      if (item.fee < 1) {
        curCount = 0;
      }
    }
    return (
      <p>
        {item.name}
        {curCount ? `(${curCount})` : ''}
      </p>
    );
  });
};
export const ProductOrderDetailModal: React.FC = () => {
  const { show, data, scheduleContext, close } = useProductOrderDetailModalStore();
  const { isMobile } = useViewport();
  const combinedRefundItems = combinedRefundItemsByName(data?.refunds);

  const REFUND_STATUS: Record<string, string> = {
    partial: '부분환불',
    all: '전체환불',
    requested: '환불요청',
    accepted: '환불예정',
    denied: '환불반려',
    reserved: '환불예정',
    default: '결제완료',
  };

  const PURCHASES_INFO_ITEMS: DescriptionsItemProps[] = [
    { span: 3, label: '구매채널', children: getChannelText(data?.channel) },
    { span: 3, label: '상품명', children: data?.product?.name || '-' },
    {
      span: 2,
      label: '주문상태',
      children: REFUND_STATUS[data?.statuses?.refund || 'default'] || '결제완료',
      contentStyle: { color: data?.refunds?.length === 0 ? '' : 'red' },
    },
    {
      span: 1,
      label: '주문수량',
      children:
        data?.purchases?.reduce((prev, cur) => {
          const isNaverOrder = data?.channel === SalesChannelsType.NAVER_STORE;
          let curCount = cur?.count || 0;
          if (!isNaverOrder && !cur?.fee) {
            curCount = 0;
          }
          return prev + curCount;
        }, 0) || '-',
    },
    {
      span: 3,
      label: '주문자',
      children: `${data?.buyer?.name || '-'} (${formatPhoneNumber(data?.buyer?.phone || '')})`,
    },
    {
      span: 3,
      label: '옵션',
      children: renderPurchases(data?.purchases, data?.channel) || '-',
    },
    {
      span: 3,
      label: '환불요청일시',
      children: data?.refund?.requestedAt ? moment(data.refund.requestedAt).format('YYYY. MM. DD. HH:mm') : '-',
      style: {
        display:
          data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ||
          data?.statuses?.refund === PaymentStatementRefundStatus.DENIED
            ? ''
            : 'none',
      },
    },
    {
      span: 3,
      label: '환불요청옵션',
      children:
        data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div>{renderPurchases(data?.refund?.refundItems || data?.refund?.purchases, data?.channel) || '-'}</div>
            {data?.refund?.requestedAt && (
              <div style={{ color: 'gray' }}>{moment(data.refund.requestedAt).format('YYYY. MM. DD. HH:mm')}</div>
            )}
          </div>
        ) : (
          '-'
        ),
      labelStyle: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? '' : 'none',
      },
      style: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? '' : 'none',
        color: 'red',
      },
    },
    {
      span: 3,
      label: '환불요청금액',
      children:
        data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div>{data?.refund?.requestedAmount?.toLocaleString()}원</div>
          </div>
        ) : (
          '-'
        ),
      labelStyle: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? '' : 'none',
      },
      style: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.REQUESTED ? '' : 'none',
        color: 'red',
      },
    },
    {
      span: 3,
      label: '환불예정옵션',
      children:
        data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div>{renderPurchases(data?.refund?.refundItems || data?.refund?.purchases, data?.channel) || '-'}</div>
            {data?.refund?.acceptedAt && (
              <div style={{ color: 'gray' }}>{moment(data.refund.acceptedAt).format('YYYY. MM. DD. HH:mm')}</div>
            )}
          </div>
        ) : (
          '-'
        ),
      labelStyle: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? '' : 'none',
      },
      style: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? '' : 'none',
        color: 'red',
      },
    },
    {
      span: 3,
      label: '환불예정금액',
      children:
        data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div>{data?.refund?.acceptedAmount?.toLocaleString()}원</div>
          </div>
        ) : (
          '-'
        ),
      labelStyle: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? '' : 'none',
      },
      style: {
        display: data?.statuses?.refund === PaymentStatementRefundStatus.ACCEPTED ? '' : 'none',
        color: 'red',
      },
    },
    {
      span: 3,
      label: '환불옵션',
      children: (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <div>{renderPurchases(combinedRefundItems, data?.channel) || '-'}</div>
          {data?.refund?.refundedAt && (
            <div style={{ color: 'gray' }}>{moment(data.refund.refundedAt).format('YYYY. MM. DD. HH:mm')}</div>
          )}
        </div>
      ),
      labelStyle: { display: data?.refunds && data.refunds.length !== 0 ? '' : 'none' },
      style: { display: data?.refunds && data.refunds.length !== 0 ? '' : 'none', color: 'red' },
    },
    {
      span: 3,
      label: '환불금액',
      children: (() => {
        const partnerRefundAmount = (data?.refund?.amount || 0) + (data?.refund?.membershipDiscountAmount || 0);
        // 멤버십 할인율 추출
        const discountPercent = data?.buyer?.membership?.discountPercent || 0;
        // 고객 환불금액 계산
        const customerRefundAmount = data?.refund?.amount || 0;

        if (partnerRefundAmount !== customerRefundAmount) {
          return (
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <div>
                  <span style={{ fontWeight: 500 }}>환불금액: </span>
                  {`${partnerRefundAmount.toLocaleString()}원`}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 4,
                }}>
                <div>
                  <span style={{ fontWeight: 500, color: 'red' }}>고객 환불금액: </span>
                  {`${customerRefundAmount.toLocaleString()}원`}
                  {discountPercent > 0 && (
                    <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                      (멤버십 할인율 {discountPercent}% 적용)
                    </span>
                  )}
                </div>
              </div>
              {discountPercent > 0 && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  멤버십 할인율이 적용되어 고객에게는 위 금액이 환불됩니다.
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div>
              <span style={{ fontWeight: 500, color: 'red' }}></span>
              {`${customerRefundAmount.toLocaleString()}원`}
            </div>
          );
        }
      })(),
      labelStyle: { display: data?.refunds && data.refunds.length !== 0 ? '' : 'none' },
      style: { display: data?.refunds && data.refunds.length !== 0 ? '' : 'none', color: 'red' },
    },
    { span: 3, label: '입력옵션', children: <div>{data?.memo?.qna || '-'}</div> },
    {
      span: 3,
      label: '결제금액',
      children: getPaymentAmountWithMembershipDiscountText(data?.payment),
    },
    {
      span: 3,
      label: '요청사항',
      children: (
        <div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{data?.memo?.payment || '-'}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{data?.memo?.shipping || ''}</div>
        </div>
      ),
    },
  ];
  const SHIPPING_INFO_ITEMS: DescriptionsItemProps[] = [
    { span: 3, label: '수취인', children: data?.shipping?.name || '-' },
    { span: 3, label: '연락처', children: data?.shipping?.phone || '-' },
    { span: 3, label: '배송지', children: `${data?.shipping?.address || '-'} ${data?.shipping?.addressDetail || ''}` },
  ];

  const scheduleInfo = scheduleContext?.scheduleInfo;
  const PARTICIPANT_INPUT_ITEMS: DescriptionsItemProps[] = [
    { span: 3, label: '회차', children: scheduleContext?.optionName || '-' },
    {
      span: 3,
      label: '플랫폼',
      children: scheduleContext?.sourceName || getChannelText(data?.channel) || '-',
    },
    { span: 3, label: '참여자명', children: scheduleInfo?.name || '-' },
    {
      span: 3,
      label: '학년',
      children: scheduleInfo?.schoolYear
        ? scheduleInfo.birthYear
          ? `${scheduleInfo.birthYear} (${scheduleInfo.schoolYear})`
          : scheduleInfo.schoolYear
        : scheduleInfo?.birthYear || '-',
    },
    { span: 3, label: '함께하는 친구', children: scheduleInfo?.friends || '-' },
    {
      span: 3,
      label: '구매 시 입력',
      children: (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {scheduleContext?.notes || scheduleInfo?.notes || '-'}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={scheduleContext ? '결제 내역' : '주문상세'}
      open={show}
      onCancel={close}
      width={isMobile ? '100vw' : 560}
      cancelText="닫기"
      centered
      okButtonProps={{ style: { display: 'none' } }}
      styles={{ content: { height: isMobile ? '100vh' : '95vh' } }}>
      <div className="payment-detail-modal">
        {scheduleContext && (
          <Descriptions
            title="참여자 입력 정보"
            items={PARTICIPANT_INPUT_ITEMS}
            bordered
            labelStyle={{ width: 150, alignContent: 'center' }}
            style={{ whiteSpace: 'pre-wrap', marginBottom: 24 }}
          />
        )}
        <Descriptions
          title="구매 정보"
          items={PURCHASES_INFO_ITEMS}
          bordered
          labelStyle={{ width: 150, alignContent: 'center' }}
          style={{ whiteSpace: 'pre-wrap' }}
        />
        {data?.shipping && (
          <Descriptions
            title="배송 정보"
            items={SHIPPING_INFO_ITEMS}
            bordered
            labelStyle={{ width: 150, alignContent: 'center' }}
          />
        )}
      </div>
    </Modal>
  );
};

export default ProductOrderDetailModal;
